import React, { useState, useEffect, useCallback } from 'react'; // eslint-disable-line
import './Admin.css';

const formatPrice = (p) => '৳' + Number(p).toLocaleString('en-BD');

const STATUS_OPTIONS = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const STATUS_META = {
  pending:   { label: 'Pending',   color: '#d97706', bg: '#fef3c7' },
  confirmed: { label: 'Confirmed', color: '#2563eb', bg: '#eff6ff' },
  shipped:   { label: 'Shipped',   color: '#7c3aed', bg: '#f5f3ff' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4' },
  cancelled: { label: 'Cancelled', color: '#e63946', bg: '#fff5f5' },
};

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const Admin = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('admin_authed') === '1');
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState('');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/orders.php?admin=1`);
      const data = await res.json();
      if (data.success) {
        setOrders(data.orders);
      } else {
        setError(data.message || 'Failed to load orders.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchOrders();
  }, [authed, fetchOrders]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (pwInput === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin_authed', '1');
      setAuthed(true);
    } else {
      setPwError('Incorrect password');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      const res = await fetch(`${API_BASE}/orders.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders((prev) =>
          prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o)
        );
      } else {
        alert(data.message || 'Update failed');
      }
    } catch {
      alert('Network error.');
    } finally {
      setUpdatingId(null);
    }
  };

  // ─── Login screen ────────────────────────────────────
  if (!authed) {
    return (
      <div className="adm-login-page">
        <div className="adm-login-card">
          <div className="adm-login-icon">🔐</div>
          <h2>Admin Panel</h2>
          <p>FashionAR — Order Management</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin password"
              value={pwInput}
              onChange={(e) => { setPwInput(e.target.value); setPwError(''); }}
              className="adm-login-input"
              autoFocus
            />
            {pwError && <div className="adm-login-error">{pwError}</div>}
            <button type="submit" className="adm-login-btn">Login</button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Dashboard ────────────────────────────────────────
  const filtered = filterStatus === 'all' ? orders : orders.filter((o) => o.status === filterStatus);

  const stats = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = orders.filter((o) => o.status === s).length;
    return acc;
  }, {});

  const totalRevenue = orders
    .filter((o) => o.status !== 'cancelled')
    .reduce((sum, o) => sum + Number(o.total_amount), 0);

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div className="adm-header-brand">🛍 FashionAR Admin</div>
        <div className="adm-header-right">
          <button className="adm-refresh-btn" onClick={fetchOrders} disabled={loading}>
            {loading ? '⟳ Loading...' : '⟳ Refresh'}
          </button>
          <button className="adm-logout-btn" onClick={() => { sessionStorage.removeItem('admin_authed'); setAuthed(false); }}>
            Logout
          </button>
        </div>
      </header>

      <div className="adm-content">
        {/* Stats */}
        <div className="adm-stats">
          <div className="adm-stat-card adm-stat-total">
            <div className="adm-stat-val">{orders.length}</div>
            <div className="adm-stat-label">Total Orders</div>
          </div>
          <div className="adm-stat-card adm-stat-revenue">
            <div className="adm-stat-val">{formatPrice(totalRevenue)}</div>
            <div className="adm-stat-label">Total Revenue</div>
          </div>
          {STATUS_OPTIONS.map((s) => {
            const m = STATUS_META[s];
            return (
              <div className="adm-stat-card" key={s} style={{ borderTop: `3px solid ${m.color}` }}>
                <div className="adm-stat-val" style={{ color: m.color }}>{stats[s]}</div>
                <div className="adm-stat-label">{m.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filter */}
        <div className="adm-filter-bar">
          <span className="adm-filter-label">Filter:</span>
          {['all', ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              className={`adm-filter-btn ${filterStatus === s ? 'active' : ''}`}
              onClick={() => setFilterStatus(s)}
            >
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
        </div>

        {error && <div className="adm-error">{error}</div>}

        {/* Orders table */}
        {filtered.length === 0 && !loading ? (
          <div className="adm-empty">No orders found.</div>
        ) : (
          <div className="adm-orders">
            {filtered.map((order) => {
              const meta = STATUS_META[order.status] || STATUS_META.pending;
              const isExpanded = expandedId === order.id;

              return (
                <div className="adm-order-card" key={order.id}>
                  <div className="adm-order-row" onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                    <div className="adm-order-id">#{String(order.id).padStart(5, '0')}</div>
                    <div className="adm-order-customer">
                      <div>{order.customer_name}</div>
                      <div className="adm-order-phone">{order.customer_phone}</div>
                    </div>
                    <div className="adm-order-amount">{formatPrice(order.total_amount)}</div>
                    <div className="adm-order-date">
                      {new Date(order.created_at).toLocaleDateString('en-BD', { day:'numeric', month:'short', year:'numeric' })}
                    </div>
                    <div className="adm-order-status" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        disabled={updatingId === order.id}
                        style={{ color: meta.color, background: meta.bg }}
                        className="adm-status-select"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{STATUS_META[s].label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="adm-expand-icon">{isExpanded ? '▲' : '▼'}</div>
                  </div>

                  {isExpanded && (
                    <div className="adm-order-detail">
                      <div className="adm-detail-cols">
                        <div>
                          <div className="adm-detail-section-title">Customer</div>
                          <div className="adm-detail-info">
                            <div><strong>Name:</strong> {order.customer_name}</div>
                            <div><strong>Phone:</strong> {order.customer_phone}</div>
                            {order.customer_email && <div><strong>Email:</strong> {order.customer_email}</div>}
                            <div><strong>Address:</strong> {order.customer_address}, {order.customer_district}</div>
                            {order.note && <div><strong>Note:</strong> {order.note}</div>}
                          </div>
                        </div>
                        <div>
                          <div className="adm-detail-section-title">Payment</div>
                          <div className="adm-detail-info">
                            <div><strong>Method:</strong> {order.payment_method === 'cod' ? 'Cash on Delivery' : order.payment_method}</div>
                            <div><strong>Total:</strong> {formatPrice(order.total_amount)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="adm-detail-section-title" style={{ marginTop: 16 }}>Items</div>
                      <div className="adm-detail-items">
                        {(order.items || []).map((item, i) => (
                          <div className="adm-detail-item" key={i}>
                            {item.product_image && <img src={item.product_image} alt={item.product_name} />}
                            <div className="adm-detail-item-info">
                              <div>{item.product_name}</div>
                              <div className="adm-detail-item-meta">Qty: {item.quantity} × {formatPrice(item.price)}</div>
                            </div>
                            <div className="adm-detail-item-sub">{formatPrice(item.price * item.quantity)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
